/**
 * 처리창의 공통 머리 칩 — $j.F.Admin.TaskBadge
 *
 * 한 건을 열어 처리하는 창(판정창·심사창·처리창)은 도메인마다 본문이 제각각이다.
 * 그런데 **"이 건은 누가 보고 있나"** 는 어느 창에서나 같은 질문이고, 답은 원장에만 있다.
 * 본문에 끼워넣으면 창마다 자리를 새로 정해야 하므로, **세 창이 이미 공유하는 자리**에 붙인다 —
 * `$j.Ui.Modal` 의 머리줄(`.ui-modal__header`)이다(JBModal 도 그 위에 서 있다).
 *
 *   ┌────────────────────────────────────────────────┐
 *   │ 부정발주 케이스 #112   [👤 이재일 · 처리중]  ✕ │
 *   ├────────────────────────────────────────────────┤
 *   │ (도메인 본문 — 손대지 않는다)                   │
 *
 * 칩을 누르면 담당·상태·경과와 **처리 이력**이 펼쳐지고, 거기서 맡거나 넘겨받을 수 있다.
 *
 *   $j.F.Admin.TaskBadge.attach(handle.el, 'collusion', caseNum, { onChanged });
 *
 * ⚠ 원장에 없는 건이면 **아무것도 붙이지 않는다** — 원장 밖 종류의 창에 빈 칩이 뜨면
 *   "담당이 없다" 가 아니라 "이 화면이 고장났다" 로 읽힌다.
 *
 * 여는 순간의 담당 확인은 여전히 `$j.F.Admin.TaskGuard` 가 한다. 이 칩은 **연 뒤에** 쓰는 문이다 —
 * 보기만 하려고 열었다가 "내가 하겠다" 로 마음이 바뀌는 일이 실제로 잦다.
 * 규약은 .ai/domains/admin-task.md 「한 건을 여는 프로토콜」.
 */
export default class TaskBadge {

    static STATE = { new: '신규', doing: '처리중', done: '완료', skip: '제외' };

    /**
     * @param {jQuery|Element} modalEl 모달 루트 (Ui.Modal 핸들의 el)
     * @param {string} type   원장 종류 코드 (BadgeSource::TYPE)
     * @param {number} refId  소스 행 번호
     * @param {{onChanged?:Function, task?:object}} [opts]
     *        task 를 넘기면 조회를 건너뛴다 — TaskGuard 가 방금 읽은 값을 그대로 물려줄 때 쓴다
     */
    static async attach(modalEl, type, refId, opts = {}) {
        this._css();

        const t = opts.task !== undefined ? opts.task : await this._fetch(type, refId);
        if (!t) return null;                       // 원장 밖의 건 — 붙이지 않는다

        this._draw($(modalEl), type, refId, t, opts);
        this._lock($(modalEl), type, refId, t, opts);
        return t;
    }

    /**
     * **보기 전용 잠금** — 남이 처리중인 건을 「보기만」으로 열었을 때(TaskGuard).
     * 창이 `lockOnView` 로 열었으면 담당 없는 건(푸는 길은 맡기)과 원장에서 닫힌 건도 잠긴다
     * (제외는 다시 열면 담당 없는 건이 되고, 완료는 푸는 길이 없다 — 소스가 열려 있으면 동기화가 되살린다).
     *
     * 처리창 본문은 도메인마다 제각각이라 버튼을 하나하나 끄지 않는다. 창 루트에 **캡처 단계**
     * 클릭 가로채기를 하나 걸어, 본문의 처리 버튼(`button`·`.dvr-act`)이 눌리면 막고 넘겨받을지
     * 묻는다. 늦게 그려지는 본문(악덕 심사창은 비동기로 붙는다)도 같은 루트 밑이라 함께 걸린다.
     * 보기만 하는 버튼(내역 펼치기 등)은 도메인 쪽에서 `data-view-ok` 를 달아 통과시킨다.
     *
     * 넘겨받으면(여기서든 칩 팝오버에서든) 풀린다. 한 창에서 여러 건을 오가는 화면(악덕)은
     * attach 가 건마다 다시 불리므로, 건을 바꿀 때마다 잠금도 새로 정해진다.
     */
    static _lock($modal, type, refId, t, opts) {
        const el = $modal[0];
        if (!el) return;

        const open = t.state === 'new' || t.state === 'doing';
        const on   = (!open || !t.is_mine) && $j.F.Admin.TaskGuard.isViewOnly(type, refId);

        $modal.find('.tkb-view').remove();
        $modal.toggleClass('is-view-only', on);
        el._tkbLock = on ? { type, refId, t, opts } : null;

        if (on) {
            const say = this._lockSay(t);
            $modal.find('.ui-modal__header').first().after(`
                <div class="tkb-view">
                    <span><i class="fas fa-eye"></i> 보기 전용 · ${this._esc(say.band)}</span>
                    ${say.go ? `<button type="button" class="tkb-view__take">${say.go}</button>` : ''}
                </div>`);
        }

        if (el._tkbLockBound) return;
        el._tkbLockBound = true;

        el.addEventListener('click', (e) => {
            const lock = el._tkbLock;
            if (!lock) return;

            const $t = $(e.target);
            if ($t.closest('.tkb-view__take').length) {
                e.preventDefault(); e.stopPropagation();
                this._takeOver($modal, lock);
                return;
            }
            // 머리줄(칩·✕)과 보기 전용 버튼은 통과
            if ($t.closest('.ui-modal__header, [data-view-ok]').length) return;
            if (!$t.closest('button, .dvr-act').length) return;

            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation();
            this._askTake($modal, lock);
        }, true);
    }

    /** 잠긴 까닭과 푸는 길 — 띠·확인창이 같은 말을 쓴다. go 가 없으면 여기서 풀 수 없다 */
    static _lockSay(t) {
        if (t.state === 'skip') return { band: '제외된 건', title: '제외된 건입니다.', desc: '처리하려면 먼저 다시 열어 주세요.', go: '다시 열기', api: 'reopen' };
        if (t.state === 'done') return { band: '완료된 건', title: '완료된 건입니다.', desc: '여기서는 처리할 수 없습니다.', go: '', api: '' };
        if (t.assignee) return { band: `${t.assignee_name} 님이 처리중`, title: `${t.assignee_name} 님이 처리중입니다.`, desc: '', go: '이어서 처리', api: 'takeover' };
        return { band: '담당자 없음', title: '열람으로 열었습니다.', desc: '처리하려면 먼저 담당을 맡아 주세요.', go: '담당', api: 'claim' };
    }

    static async _askTake($modal, lock) {
        const say = this._lockSay(lock.t);
        const go = await $j.Ui.Dialog.open({
            maxWidth: 400,
            title:    say.title,
            desc:     say.desc,
            confirm:  say.go || '확인',
            ...(say.go ? { cancel: '닫기' } : {}),
        });
        if (go && say.go) this._takeOver($modal, lock);
    }

    /** 넘겨받고 잠금을 푼다 — 칩·목록도 함께 갱신 */
    static async _takeOver($modal, lock) {
        const { type, refId, t, opts } = lock;
        const { api } = this._lockSay(t);
        const r = await $.post('/adm/ajax/tasks/' + api, { num: t.num });
        if (!r || !r.result) {
            $j.Ui.Toast.show((r && r.message) || '처리하지 못했습니다.', { type: 'warn' });
            return;
        }
        // 다시 열면 담당 없는 건이 될 뿐이다 — 잠금은 맡을 때 풀린다
        $j.Ui.Toast.show(api === 'reopen' ? '다시 열었습니다. 담당을 맡으면 처리할 수 있습니다.'
            : (api === 'claim' ? '담당을 맡았습니다.' : '넘겨받았습니다.') + ' 이제 처리할 수 있습니다.', { type: 'success' });

        if (api !== 'reopen') $j.F.Admin.TaskGuard.clearViewOnly(type, refId);
        const next = await this._fetch(type, refId);
        if (next) {
            this._draw($modal, type, refId, next, opts);
            this._lock($modal, type, refId, next, opts);
        }
        if (typeof opts.onChanged === 'function') opts.onChanged();
    }

    static _fetch(type, refId) {
        return $.post('/adm/ajax/tasks/lookup', { type, ref: refId })
            .then((r) => (r && r.task) || null)
            .catch(() => null);                    // 원장을 못 읽는 것으로 처리를 막지 않는다
    }

    /** 머리줄에 칩을 세운다. 다시 부르면 갈아끼운다(담당이 바뀐 뒤 갱신) */
    static _draw($modal, type, refId, t, opts) {
        const $head = $modal.find('.ui-modal__header').first();
        if (!$head.length) return;

        const who   = t.state === 'done' || t.state === 'skip'
            ? (t.done_name || '자동')
            : (t.assignee_name || '미배정');
        const state = TaskBadge.STATE[t.state] || t.state;

        const $chip = $(`
            <button type="button" class="tkb tkb--${t.state}" title="담당·처리 이력 보기">
                <i class="fas fa-user-check"></i><b>${this._esc(who)}</b><em>${this._esc(state)}</em>
            </button>`);

        $head.find('.tkb').remove();
        // ✕ 앞에 세운다 — 머리줄이 space-between 이라 margin-left:auto 로 오른쪽에 붙인다
        const $close = $head.find('.ui-modal__close');
        $close.length ? $close.before($chip) : $head.append($chip);

        $chip.on('click', (e) => {
            e.preventDefault(); e.stopPropagation();
            this._popover(e.currentTarget, $modal, type, refId, t, opts);
        });
    }

    /** 칩 팝오버 — 요약 + 처리 이력 + 맡기/넘겨받기 */
    static async _popover(anchor, $modal, type, refId, t, opts) {
        const mine   = !!t.is_mine;
        const closed = t.state === 'done' || t.state === 'skip';
        const when   = (t.opened_at || '').slice(0, 16);
        const days   = Math.floor((t.elapsed_h || 0) / 24);

        // 맡는 문은 **닫히지 않은 건에만** 낸다 — 닫힌 건에 담당은 뜻이 없다
        let act = '';
        if (!closed && !mine) {
            act = t.assignee
                ? `<button type="button" class="tkb-act" data-act="takeover">${this._esc(t.assignee_name)} 님에게서 넘겨받기</button>`
                : `<button type="button" class="tkb-act" data-act="claim">담당</button>`;
        }

        $j.Ui.Tooltip.show(anchor, `
            <div class="tkb-pop">
                <dl>
                    <dt>담당</dt><dd>${this._esc(t.assignee_name || '미배정')}${mine ? ' <small>(나)</small>' : ''}</dd>
                    <dt>상태</dt><dd>${this._esc(TaskBadge.STATE[t.state] || t.state)}${t.done_name ? ' · ' + this._esc(t.done_name) : ''}</dd>
                    <dt>발생</dt><dd>${this._esc(when)}${days ? ` <small>(${days}일 경과)</small>` : ''}</dd>
                </dl>
                ${act}
                <div class="tkb-hist">이력을 불러오는 중…</div>
            </div>`, { tone: 'panel', maxWidth: 340 });

        this._logs(t.num);

        // 팝오버는 body 밑에 붙으므로 문서 위임으로 받는다(같은 팝오버를 다시 열어도 한 벌)
        $(document).off('click.tkbact').on('click.tkbact', '.tkb-act', async (e) => {
            const act = $(e.currentTarget).data('act');
            const r   = await $.post('/adm/ajax/tasks/' + (act === 'claim' ? 'claim' : 'takeover'), { num: t.num });
            if (r && r.message) $j.Ui.Toast.show(r.message, { type: r.result ? 'success' : 'warn' });

            $j.Ui.Tooltip.hide();

            // 칩을 다시 그린다 — 방금 바뀐 담당이 그 자리에서 보여야 한다.
            // 넘겨받았으면 보기 전용도 풀린다
            if (r && r.result) $j.F.Admin.TaskGuard.clearViewOnly(type, refId);
            const next = await this._fetch(type, refId);
            if (next) { this._draw($modal, type, refId, next, opts); this._lock($modal, type, refId, next, opts); }

            // 이 창을 연 목록도 담당이 바뀐 것을 알아야 한다
            if (typeof opts.onChanged === 'function') opts.onChanged();
        });
    }

    /** 처리 이력 — 간략히 세 줄. 자세히는 모아보기가 보여준다 */
    static async _logs(num) {
        let rows = [];
        try {
            const r = await $.post('/adm/ajax/tasks/logs', { num });
            rows = (r && r.data) || [];   // TaskController@logs 는 data 로 준다
        } catch (e) { /* 이력을 못 읽어도 칩은 제 일을 한다 */ }

        const $box = $('.tkb-hist');
        if (!$box.length) return;

        if (!rows.length) { $box.html('<span class="text-muted">기록이 없습니다.</span>'); return; }

        $box.html('<b>처리 이력</b>' + rows.slice(0, 3).map((l) => `
            <div class="tkb-hist__i">
                <span>${this._esc((l.created_at || '').slice(5, 16))}</span>
                <b>${this._esc(l.manage_name || '시스템')}</b>
                <em>${this._esc(TaskBadge.STATE[l.to_state] || l.to_state || '')}</em>
            </div>`).join(''));
    }

    /** 모양 — 1회만 주입(id 가드). 모달 안이라 어느 화면 위에서도 떠야 한다 */
    static _css() {
        if (document.getElementById('task-badge-style')) return;
        const el = document.createElement('style');
        el.id = 'task-badge-style';
        el.textContent = `
.ui-modal__header .tkb{display:inline-flex;align-items:center;gap:6px;margin-left:auto;
  padding:3px 10px;border:1px solid var(--adm-border);border-radius:var(--adm-radius-chip,999px);
  background:var(--adm-surface);color:inherit;font-size:12px;line-height:1.6;cursor:pointer}
.ui-modal__header .tkb:hover{background:var(--adm-row-hover)}
.ui-modal__header .tkb i{font-size:11px;color:var(--adm-text-faint)}
.ui-modal__header .tkb b{font-weight:600}
.ui-modal__header .tkb em{font-style:normal;font-size:11px;color:var(--adm-text-muted)}
/* 아직 아무도 안 맡은 건 — 눈에 걸려야 한다 */
.ui-modal__header .tkb--new{background:var(--adm-tint-amber);border-color:var(--adm-edge-amber);color:var(--adm-ink-amber)}
.ui-modal__header .tkb--new em,.ui-modal__header .tkb--new i{color:inherit;opacity:.8}
.ui-modal__header .tkb--done,.ui-modal__header .tkb--skip{color:var(--adm-text-muted)}

/* 보기 전용 띠 — 머리줄 바로 밑. 처리 버튼이 왜 안 먹는지 먼저 보이게 */
.tkb-view{display:flex;align-items:center;justify-content:space-between;gap:10px;
  padding:7px 14px;font-size:12.5px;
  background:var(--adm-tint-amber);color:var(--adm-ink-amber);border-bottom:1px solid var(--adm-edge-amber)}
.tkb-view i{margin-right:4px}
.tkb-view__take{flex:none;padding:3px 10px;font-size:12px;font-weight:600;cursor:pointer;
  border:1px solid var(--adm-edge-amber);border-radius:var(--adm-radius-sm);
  background:var(--adm-surface);color:var(--adm-ink-amber)}
.tkb-view__take:hover{background:var(--adm-row-hover)}
.tkb-pop{font-size:12.5px;text-align:left}
.tkb-pop dl{display:grid;grid-template-columns:44px 1fr;gap:3px 10px;margin:0}
.tkb-pop dt{font-weight:700;color:var(--adm-text-muted)}
.tkb-pop dd{margin:0;color:var(--adm-text-heading)}
.tkb-pop small{color:var(--adm-text-faint)}
.tkb-act{display:block;width:100%;margin-top:9px;padding:6px 10px;
  border:1px solid var(--adm-border-strong);border-radius:var(--adm-radius-sm);
  background:var(--adm-surface);color:var(--adm-text-heading);font-size:12.5px;font-weight:600;cursor:pointer}
.tkb-act:hover{background:var(--adm-row-hover)}
.tkb-hist{margin-top:9px;padding-top:8px;border-top:1px dashed var(--adm-border);
  font-size:11.5px;color:var(--adm-text-secondary)}
.tkb-hist b{color:var(--adm-text-heading)}
.tkb-hist__i{display:flex;gap:8px;margin-top:3px}
.tkb-hist__i span{flex:none;color:var(--adm-text-faint);font-variant-numeric:tabular-nums}
.tkb-hist__i em{margin-left:auto;font-style:normal;color:var(--adm-text-muted)}
`;
        document.head.appendChild(el);
    }

    static _esc(v) {
        return String(v == null ? '' : v).replace(/[&<>"']/g, (c) =>
            ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
    }
}
